import { useParams, Link } from 'react-router-dom';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import { ComicCard } from '@/pages/home/components/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const relatedComics = getRelatedComics(id || '', 6);

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-heading text-foreground-800 mb-2">漫画不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    image: comic.coverImage,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updateDate || '',
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: '妖精漫画网',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是免费的吗？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `是的，${comic.title}在妖精漫画网上完全免费在线观看，支持下拉式阅读模式，无需任何付费。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}更新频率如何？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}目前状态为${comic.status}，作者${comic.author}保持稳定更新节奏，建议收藏关注最新动态。`,
        },
      },
      {
        '@type': 'Question',
        name: '如何在妖精漫画网上找到更多类似的作品？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以通过妖精漫画网的${comic.category}分类浏览更多同类韩国漫画，或者使用搜索功能发现感兴趣的作品。`,
        },
      },
    ],
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://lvtianyuan.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: comic.category,
        item: `http://lvtianyuan.com/category/${comic.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: `http://lvtianyuan.com/comic/${comic.id}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title}_${comic.author}_${comic.category}韩国漫画_妖精漫画网`}
        description={`妖精漫画网为您提供${comic.title}在线免费阅读。${comic.description}作者${comic.author}，${comic.status}，共${comic.totalChapters}话。下拉式阅读体验。`}
        keywords={`${comic.title},${comic.author},${comic.category}韩国漫画,妖精漫画网,在线观看`}
        canonicalUrl={`http://lvtianyuan.com/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        jsonLd={[articleJsonLd, faqJsonLd, breadcrumbJsonLd]}
      />

      <main className="pt-20">
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 mt-4">
            <div className="lg:w-[300px] shrink-0">
              <div className="rounded-lg overflow-hidden bg-background-100 aspect-[3/4] sticky top-24">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title}封面`}
                  title={`${comic.title} - 妖精漫画网韩国漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {comic.isHot && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">HOT</span>
                )}
                <span className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">{comic.category}</span>
                <span className="px-2.5 py-1 rounded-full text-xs bg-primary-100 text-primary-700">{comic.status}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">{comic.title}</h1>
              <p className="text-sm text-foreground-500 mb-4">
                作者：<span className="text-foreground-700 font-medium">{comic.author}</span>
              </p>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="text-foreground-800">{comic.rating}</strong> 分
                </span>
                <span>
                  <i className="ri-eye-line mr-1"></i>
                  {(comic.views / 10000).toFixed(1)}万 阅读
                </span>
                <span>
                  <i className="ri-heart-line mr-1"></i>
                  {(comic.favorites / 10000).toFixed(1)}万 收藏
                </span>
                <span>
                  共 <strong className="text-foreground-800">{comic.totalChapters}</strong> 话
                </span>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-4 p-4 bg-background-100/70 rounded-lg">
                {comic.description}
              </p>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Link
                  to={`/comic/${comic.id}/chapter/${comic.chapters[0]?.id || '1'}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读 <i className="ri-play-line"></i>
                </Link>
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line"></i> 加入书架
                </button>
                <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>

              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-list-check text-accent-500"></i>
                  章节列表
                  <span className="text-sm font-normal text-foreground-500">
                    (共{comic.totalChapters}话 · {comic.status})
                  </span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto pr-2">
                  {comic.chapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}/chapter/${ch.id}`}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all text-sm cursor-pointer"
                    >
                      <span className="text-foreground-700 truncate">{ch.title}</span>
                      {ch.updateDate === comic.updateTime && (
                        <span className="text-[10px] text-accent-500 shrink-0 ml-1">NEW</span>
                      )}
                    </Link>
                  ))}
                  {comic.chapters.length < comic.totalChapters && (
                    <div className="flex items-center justify-center px-3 py-2.5 rounded-lg border border-dashed border-background-300 text-foreground-400 text-sm">
                      更多章节加载中...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100/70 py-14 md:py-16 mt-8">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              猜你
              <span className="text-accent-500">喜欢</span>
            </h2>
            <p className="text-sm text-foreground-500 mb-6">更多{comic.category}类韩国漫画推荐</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
            加入
            <span className="italic font-normal text-accent-500"> 妖精漫画</span> 社区
          </h2>
          <p className="text-sm text-foreground-500 mb-6">和其他漫画爱好者一起讨论你喜欢的作品</p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            了解更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </section>
      </main>
    </>
  );
}